// 挂件会话选择器 + 多挂件挑选（§6.7）
import { useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { resolveWidgetTabs } from '@/activity-orchestration/defaults';
import { isActivityShowable, useActivityStore } from '@/store/activity';
import { useActivityQueueStore } from '@/store/activity-queue';

import { useActivitySessionStore } from './slice';

import type { IActivity, IActivitySession, TDisplayTab } from '@/activity-orchestration/types';

/** 来源排序权重：源 2（关窗后）优先展示，源 1（冷启）其次（§6.5） */
const sourceRank = (session: IActivitySession): number => (session.widgetSource === 'popup_dismiss' ? 0 : 1);

/**
 * 渲染时挑选可见挂件（方案 B：挑选而非写入时删除，§6.5 / §6.7）。
 * 排序键：来源(源2>源1) -> priority -> createdAt -> activityId，全序稳定、切页前后一致。
 * 过期 / 失效以 ActivityStore 为准；展示页以 widgetTabs ∩ activeScene 为准（可与弹窗 tabs 不一致）。
 */
export const computeVisibleWidgets = (
    sessions: IActivitySession[],
    maxWidgets: number,
    getActivity: (activityId: string) => IActivity | undefined,
    activeScene: TDisplayTab | null,
    now: number = Date.now()
): IActivitySession[] => {
    const priorityOf = (session: IActivitySession): number => getActivity(session.activityId)?.priority ?? 0;

    return sessions
        .filter(session => {
            if (!session.widgetVisible) {
                return false;
            }
            const activity = getActivity(session.activityId);
            if (!activity || !isActivityShowable(activity, now)) {
                return false;
            }
            // 非可展示页（activeScene=null，如裸 reader）不挂件
            if (!activeScene) {
                return false;
            }
            // 与弹窗 tabs 解耦：仅 widgetTabs 命中当前 scene 才展示
            return resolveWidgetTabs(activity.type).includes(activeScene);
        })
        .sort(
            (a, b) =>
                sourceRank(a) - sourceRank(b) || // 来源：源 2 > 源 1
                priorityOf(b) - priorityOf(a) || // 优先级：大者先（取自 ActivityStore）
                a.createdAt - b.createdAt || // 时间：早者先
                a.activityId.localeCompare(b.activityId) // 兜底，保证全序
        )
        .slice(0, maxWidgets); // 超出者暂不展示，不销毁 session
};

/** 全量挂件会话 map */
export const useActivitySessions = () => useActivitySessionStore(state => state.sessions);

/** 同屏挂件上限 */
export const useMaxWidgets = () => useActivitySessionStore(state => state.maxWidgets);

/**
 * 当前应渲染的可见挂件列表（WidgetHost 订阅）。
 * 依赖 sessions / activities / activeScene，任一变化都重算。
 */
export const useVisibleWidgets = (): IActivitySession[] => {
    const sessions = useActivitySessionStore(state => state.sessions);
    const maxWidgets = useActivitySessionStore(state => state.maxWidgets);
    const activities = useActivityStore(state => state.activities);
    const activeScene = useActivityQueueStore(state => state.activeScene);

    return useMemo(
        () => computeVisibleWidgets(Object.values(sessions), maxWidgets, id => activities[id], activeScene),
        [sessions, maxWidgets, activities, activeScene]
    );
};

/** 挂件会话动作集合 */
export const useActivitySessionActions = () =>
    useActivitySessionStore(
        useShallow(state => ({
            upsertSession: state.upsertSession,
            removeSession: state.removeSession,
            setMaxWidgets: state.setMaxWidgets,
            reset: state.reset,
        }))
    );
